import { EyeIcon, EyeSlashIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import Suffix from '@tebuto/forms/Suffix'
import { cn } from '@tebuto/lib/utils'
import { Field, FieldAttributes } from 'formik'
import { ChevronDown } from 'lucide-react'
import { type JSX, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import Description from './Description'
import ErrorMessage from './ErrorMessage'
import { Label } from './Label'
import { useFieldErrorVisible } from './useFieldErrorVisible'

export interface IFormField {
    name: string
    label?: string
    autoComplete?: string
    type?: string
    className?: string
    fieldClassName?: string
    labelClassName?: string
    suffix?: string
    mandatory?: boolean
    description?: string
    placeholder?: string
    readOnly?: boolean
    showErrors?: boolean
    labelTooltip?: string
    inputTooltip?: string
    height?: 'default' | 'full' | 'small'
    formatter?: <T = string>(value: T) => string
    'data-testid'?: string
}

export default function FormField({
    className = '',
    fieldClassName,
    labelClassName,
    label,
    name,
    autoComplete,
    suffix,
    type,
    labelTooltip,
    inputTooltip,
    description,
    placeholder,
    mandatory,
    readOnly,
    showErrors = true,
    height = 'default',
    formatter: _formatter,
    'data-testid': testId,
    ...rest
}: FieldAttributes<IFormField>): JSX.Element {
    const showFieldError = useFieldErrorVisible(name)
    const [contentHidden, setContentHidden] = useState(type === 'password')
    const isWidgetField = fieldClassName?.includes('widget-text-primary') ?? false
    const inputTooltipId = inputTooltip ? `${name}-input` : undefined

    function CSSClasses() {
        let defaultClasses =
            'h-full rounded-lg flex-1 min-w-0 block w-full px-2 py-1 border text-sm border-secondary text-foreground-tertiary shadow-xs focus:outline-none focus:ring-0 focus:border-primary-500'
        if (!isWidgetField) {
            defaultClasses += ' placeholder:text-foreground-muted'
        }
        if (readOnly) defaultClasses += ' hover:cursor-not-allowed '
        if (showFieldError) defaultClasses += ' border-red-500 '
        if (suffix) defaultClasses += ' rounded-r-none '
        return defaultClasses
    }

    function getHeight() {
        if (rest.as === 'textarea') return 'h-auto'
        switch (height) {
            case 'full':
                return 'h-full'
            case 'small':
                return 'h-8'
            default:
                return 'h-10'
        }
    }

    return (
        <div className={cn('relative', className)}>
            {label ? (
                <Label htmlFor={name} mandatory={mandatory} className={labelClassName}>
                    {label}
                    {labelTooltip ? (
                        <QuestionMarkCircleIcon
                            className="ml-1.5 inline-block size-3.5 align-text-bottom text-foreground-muted"
                            data-tooltip-id={name}
                            data-tooltip-content={labelTooltip}
                        />
                    ) : (
                        <></>
                    )}
                </Label>
            ) : (
                <></>
            )}
            <div
                className={cn(
                    'flex rounded-md shadow-xs relative',
                    getHeight(),
                    readOnly && inputTooltip ? 'cursor-not-allowed' : ''
                )}
                {...(inputTooltipId
                    ? {
                          'data-tooltip-id': inputTooltipId,
                          'data-tooltip-content': inputTooltip
                      }
                    : {})}
            >
                <Field
                    data-tooltip-target="tooltip-default"
                    data-testid={testId ?? `field-${name}`}
                    id={name}
                    name={name}
                    type={type === 'password' ? (contentHidden ? type : 'text') : type}
                    readOnly={readOnly}
                    disabled={readOnly}
                    autoComplete={autoComplete}
                    className={cn(
                        CSSClasses(),
                        type === 'password' ? 'pr-10' : '',
                        rest.as === 'select' ? 'block! h-10 w-full min-w-0 flex-none appearance-none pr-10' : '',
                        fieldClassName
                    )}
                    required={mandatory}
                    placeholder={placeholder}
                    {...rest}
                />
                {rest.as === 'select' && (
                    <ChevronDown
                        aria-hidden
                        className="pointer-events-none absolute right-2.5 top-1/2 z-10 size-4 -translate-y-1/2 text-gray-500"
                    />
                )}
                {type === 'password' && (
                    <button
                        type="button"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center justify-center"
                        onClick={() => setContentHidden((value) => !value)}
                        tabIndex={-1}
                    >
                        {contentHidden ? (
                            <EyeSlashIcon className="h-5 w-5 text-foreground-muted inline" />
                        ) : (
                            <EyeIcon className="h-5 w-5 text-foreground-muted inline" />
                        )}
                    </button>
                )}
                {suffix && <Suffix suffix={suffix} />}
            </div>
            {labelTooltip ? (
                <Tooltip
                    id={name}
                    positionStrategy="fixed"
                    opacity={1}
                    className="z-9999! max-w-xs! rounded! bg-primary-800! p-3! text-xs! font-medium! leading-snug! text-white!"
                />
            ) : (
                <></>
            )}
            {inputTooltipId ? (
                <Tooltip
                    id={inputTooltipId}
                    positionStrategy="fixed"
                    opacity={1}
                    className="z-9999! max-w-xs! rounded! bg-primary-800! p-3! text-xs! font-medium! leading-snug! text-white!"
                />
            ) : (
                <></>
            )}
            {showErrors ? <ErrorMessage name={name} /> : <></>}
            <Description description={description} />
        </div>
    )
}
